import { Box, VStack, Text, Center, Container, Heading, Button, Image } from '@chakra-ui/react';
import { useRouteError, isRouteErrorResponse, useNavigate, useLocation } from 'react-router';
import { useEffect, useState } from 'react';
import Paths from '@/configs/router/paths';
import ErrorImage from './err.png';

export default function ErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const [storedError, setStoredError] = useState<string | null>(null);

  // Try to get error from sessionStorage (set by error handler)
  useEffect(() => {
    try {
      const chunkError = sessionStorage.getItem('chunkLoadError');
      if (chunkError) {
        setStoredError(chunkError);
        // Clear after reading
        sessionStorage.removeItem('chunkLoadError');
        sessionStorage.removeItem('errorTimestamp');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }, []);

  // Get error from route state or stored error
  const routeError = location.state?.error;
  const errorFromStorage = storedError;

  // Check if it's a dynamic import error
  const isChunkLoadError =
    (error instanceof Error &&
      (error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('ChunkLoadError'))) ||
    routeError?.includes('Failed to fetch dynamically imported module') ||
    errorFromStorage?.includes('Failed to fetch dynamically imported module');

  // Check if it's a network error
  const isNetworkError =
    (error instanceof TypeError && error.message.includes('Failed to fetch')) ||
    routeError?.includes('Failed to fetch') ||
    errorFromStorage?.includes('Failed to fetch');

  const errorMessage =
    routeError ||
    errorFromStorage ||
    (isRouteErrorResponse(error)
      ? error.statusText || error.data?.message || 'Unknown Error'
      : error instanceof Error
        ? error.message
        : 'Unknown Error');

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate(Paths.MESSAGES_CHATS);
  };

  return (
    <Box
      minH="100dvh"
      bg="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Container maxW="md" p={8}>
        <Center>
          <VStack gap={8} textAlign="center">
            <Image src={ErrorImage} alt="Error" width={400} height={200} />

            <VStack gap={4}>
              <Heading size="lg" color="gray.800" _dark={{ color: 'gray.200' }} fontWeight="bold">
                {isChunkLoadError || isNetworkError
                  ? 'Module Load Failed'
                  : 'Unexpected Application Error'}
              </Heading>

              <Text fontSize="md" color="gray.600" _dark={{ color: 'gray.400' }} lineHeight="1.6">
                {isChunkLoadError || isNetworkError
                  ? 'The application module failed to load. This may be due to a network issue or the application has been updated. Please try refreshing the page.'
                  : 'An unexpected error occurred. Please try refreshing the page if the problem persists.'}
              </Text>

              {errorMessage && (
                <Box
                  p={4}
                  bg="gray.100"
                  _dark={{ bg: 'gray.800' }}
                  borderRadius="md"
                  w="full"
                  textAlign="left"
                >
                  <Text
                    fontSize="sm"
                    color="gray.700"
                    _dark={{ color: 'gray.300' }}
                    fontFamily="mono"
                  >
                    {errorMessage}
                  </Text>
                </Box>
              )}

              <VStack gap={3} w="full" pt={4}>
                <Button colorPalette="brand" variant="solid" w="full" onClick={handleReload}>
                  Reload Page
                </Button>
                <Button colorPalette="gray" variant="outline" w="full" onClick={handleGoHome}>
                  Go to Home
                </Button>
              </VStack>
            </VStack>
          </VStack>
        </Center>
      </Container>
    </Box>
  );
}
